import { addBreadcrumb, captureMessage } from "@sentry/capacitor"

export type SyncPhase = "initialization" | "connection" | "subscription"

/** One report per sustained loss of update readiness; never retain tokens or payloads. */
export class SyncOutageReporter {
  private outage: {
    startedAt: number
    phase: SyncPhase
    lastErrorPhase: SyncPhase | null
    errorCode: number | null
    errorCount: number
    retryCount: number
    reported: boolean
    timer: ReturnType<typeof setTimeout>
  } | null = null
  private hasBeenReady = false

  get isActive(): boolean {
    return this.outage !== null
  }

  start(phase: SyncPhase): void {
    if (this.outage) {
      this.outage.phase = phase
      return
    }
    this.outage = {
      startedAt: performance.now(),
      phase,
      lastErrorPhase: null,
      errorCode: null,
      errorCount: 0,
      retryCount: 0,
      reported: false,
      timer: setTimeout(() => this.report(), 20000)
    }
  }

  retry(phase: SyncPhase): void {
    if (this.outage) {
      this.outage.phase = phase
      // Count observable application recovery attempts: startup retries, token refetches and
      // resume. SDK transport-only reconnect attempts are not exposed through its public API.
      this.outage.retryCount++
    }
  }

  error(phase: SyncPhase, code?: number): void {
    if (this.outage) {
      this.outage.lastErrorPhase = phase
      this.outage.errorCode = code ?? null
      this.outage.errorCount++
    }
  }

  cancel(): void {
    if (this.outage) {
      clearTimeout(this.outage.timer)
      this.outage = null
    }
  }

  ready(historyRecovered: boolean | null = null): void {
    const outage = this.outage
    if (outage) {
      clearTimeout(outage.timer)
      const summary = { ...this.summary(), history_recovered: historyRecovered }
      this.outage = null
      // A normal first connection is not a recovery. Brief drops after being ready, failed
      // startup attempts and reported outages each get one summary when readiness returns.
      if (this.hasBeenReady || outage.errorCount > 0 || outage.reported) {
        console.info("[sync] Live synchronization connection restored", summary)
        addBreadcrumb({ category: "sync.recovery", level: "info", data: summary })
      }
    }
    this.hasBeenReady = true
  }

  private report(): void {
    if (!this.outage || this.outage.reported) {
      return
    }
    this.outage.reported = true
    captureMessage("Live synchronization unavailable for 20 seconds", {
      level: "warning",
      fingerprint: ["sync-persistent-outage"],
      tags: { transport: "centrifugo", phase: this.outage.phase },
      contexts: { sync: this.summary() }
    })
  }

  private summary() {
    return {
      duration_ms: this.outage ? Math.round(performance.now() - this.outage.startedAt) : 0,
      phase: this.outage?.phase ?? null,
      last_error_phase: this.outage?.lastErrorPhase ?? null,
      error_code: this.outage?.errorCode ?? null,
      error_count: this.outage?.errorCount ?? 0,
      recovery_attempts: this.outage?.retryCount ?? 0,
      origin: window.location.origin,
      online: navigator.onLine
    }
  }
}
